import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import Footer from "src/components/Footer";
import Header from "src/components/Header";
import { colors } from "src/styles";
import { debugError } from "src/utils/log";
import { TimeAndAttendanceInputForm } from "../InputForm";
import { FirebaseDataService } from "src/services/Firebase/data";
import { Employee } from "src/types";
import { useNavigate, useParams } from "react-router-dom";
import { logInUser } from "src/redux/actions/user";
import { MAXIMO_TIME_ZONE } from "src/config/сonstants";
import FirebaseStorageService from "src/services/Firebase/storage";
import { cloudClockIn, cloudClockOut } from "src/services/CloudFuctions";
import { defaultChecklistsObj, reportPeriods } from "../../../constants";
import moment from "moment";
import { getErrorMsg } from "src/helpers/getErrorMsg";
import { getClockInChecklists, getReportChecklistsObject, reduceChecklists } from "src/services/ChecklistService";
import useLocation from "src/hooks/useLocation";
import { InputForm } from "src/pages/NoUserLogin/UserInfo/styles";
import Input, { InputTypes } from "src/components/Input";
import { VALIDATION_MESSAGES } from "src/constants/validation";
import { useIsMobile } from "src/hooks/useIsMobile";
import { ButtonContainer } from "src/components/DynamicFormContainer/styles";
import { ButtonWrapper } from "src/components/atoms/FormHeader/styles";
import Button from "src/components/Button";
import CustomToast from "src/components/atoms/CustomToast";

type ClockAction = "initial" | "clockIn" | "clockOut";

interface TimeAndAttendanceFormProps {
  name: string;
  logo: string;
  setName: (name: string) => void;
  setLogo: (logo: string) => void;
}

export const TimeAndAttendanceForm: React.FC<TimeAndAttendanceFormProps> = ({ name, logo, setName, setLogo }) => {
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    reset,
    clearErrors,
    formState: { isSubmitSuccessful, isValid, errors },
  } = useForm({
    mode: "all",
  });

  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [labelMessage, setLabelMessage] = useState<string>("");
  const [employeeData, setEmployeeData] = useState<Employee | null>(null);
  const [validationError, setValidationError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [locationName, setLocationName] = useState("");
  const { clientCode: clientId, locationId: locationId } = useParams();
  const [checklistsData, setChecklistsData] = useState(defaultChecklistsObj);
  const [submittedAnswers, setSubmittedAnswers] = useState<any[]>([]);
  const [isClockRequestSubmitted, setIsClockRequestSubmitted] = useState(false);
  const [clockAction, setClockAction] = useState<ClockAction>("initial");
  const [clockin, setClockin] = useState<boolean>(false);
  const navigate = useNavigate();

  const { coords: userLocation } = useLocation();
  const { isMobile } = useIsMobile();

  const getEmployeeClockedInState = useCallback(
    (employee: Employee) => {
      const onsite = employee?.onsite;

      if (onsite && typeof onsite === "object") {
        if (locationId && Object.prototype.hasOwnProperty.call(onsite, locationId)) {
          return Boolean((onsite as Record<string, unknown>)[locationId]);
        }

        return Object.values(onsite).some((value) => Boolean(value));
      }

      if (typeof onsite === "boolean") {
        return onsite;
      }

      return Boolean(employee?.onsitestate);
    },
    [locationId],
  );

  const onSubmit = handleSubmit(async (data: any) => {
    setLabelMessage("");
    setLoading(true);
    console.log("Form submitted with data:", data);
    try {
      if (locationId) {
        data.locationId = locationId;
      }
      data.cleaningDate = moment().format("YYYY-MM-DD");
      data.startTime = moment().toISOString();
      data.endTime = moment().toISOString();
      data.clockAction = clockAction;
      delete data.cleaningStartDate;
      delete data.cleaningEndDate;

      if (employeeData) {
        data.employeeId = employeeData.id || data.employeeId;
      }
      setError("");
      const answers = [[0]];
      setSubmittedAnswers(answers);
      setIsClockRequestSubmitted(true);
      reset();
    } catch (error) {
      setError(getErrorMsg(error));
      debugError(error);
    }
    setLoading(false);
  });

  const getChecklists = async () => {
    const { success, data } = await getClockInChecklists({
      clientId: clientId || "",
      locationId: locationId || "",
      sublocationType: "",
      isSublocationTypeDefault: false,
      periodId: reportPeriods.Daily,
    });

    if (!success) {
      return;
    }
    const checklistsData = reduceChecklists(data);
    setChecklistsData(checklistsData);
  };

  const getDistance = (
    coord1: { latitude: number; longitude: number },
    coord2: { latitude: number; longitude: number },
  ) => {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371e3; // metres
    const φ1 = toRad(coord1.latitude);
    const φ2 = toRad(coord2.latitude);
    const Δφ = toRad(coord2.latitude - coord1.latitude);
    const Δλ = toRad(coord2.longitude - coord1.longitude);
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const validateDistance = (
    userLocation: { lat: string; long: string } | { latitude: number; longitude: number } | null,
    locationPosition: { lat: string; long: string } | { latitude: number; longitude: number } | null,
  ) => {
    console.log("Validating distance...", userLocation, locationPosition);
    if (!userLocation || !locationPosition) {
      return;
    }

    const normalize = (loc: any) => {
      if ("lat" in loc && "long" in loc) {
        return { lat: parseFloat(loc.lat), long: parseFloat(loc.long) };
      }
      if ("latitude" in loc && "longitude" in loc) {
        return { lat: Number(loc.latitude), long: Number(loc.longitude) };
      }
      return { lat: NaN, long: NaN };
    };

    const { lat: userLat, long: userLong } = normalize(userLocation);
    const { lat: locationLat, long: locationLong } = normalize(locationPosition);

    if (Number.isNaN(userLat) || Number.isNaN(userLong) || Number.isNaN(locationLat) || Number.isNaN(locationLong)) {
      return;
    }

    const distance = getDistance(
      { latitude: userLat, longitude: userLong },
      { latitude: locationLat, longitude: locationLong },
    );

    console.log("Calculated distance (meters):", distance);

    if (distance > 500) {
      // distance in meters
      // navigate("/404");
      navigate("/error?message=You%20may%20only%20clock%20in/out%20at%20work%20location");
      setValidationError("You are too far from the location");
    }
  };

  const validateLocation = async () => {
    if (locationId) {
      setLoading(true);
      try {
        const response = await FirebaseDataService.getLocationById(locationId);
        if (!response || !response.data) {
          navigate("/404");
          return;
        }
        const location = response.data;
        setLoading(false);
        setLocationName(location.label);
        if (location && location.position) {
          validateDistance(userLocation, location.position);
        }
      } catch (error) {
        console.error("Error validating washroom:", error);
        navigate("/404");
      }
      setLoading(false);
    } else {
      navigate("/404");
    }
  };

  useEffect(() => {
    setError("");
    clearErrors();
    getChecklists();
    validateLocation();
  }, [clientId, locationId, userLocation]);

  const sendClockRequest = async (answers: any) => {
    console.log("employeeData:", employeeData);
    const clockInRequest: any = {};
    clockInRequest.checklists = getReportChecklistsObject({
      checklistsData,
      answers,
    });
    clockInRequest.timestamp = Date.now();
    clockInRequest.clientId = clientId;
    clockInRequest.locationId = locationId;
    clockInRequest.employeeId = employeeData?.employeeId || "";
    clockInRequest.clockAction = clockAction;

    if (clockAction === "clockIn") {
      // console.log("Sending clock-in request:", clockInRequest);
      const res: any = await cloudClockIn(clockInRequest);
      if (res.status === "success") {
        setLabelMessage(`You have been successfully clocked in at: ${moment().format("h:mm A")}`);
        setSubmitSuccess(true);
        setError("");
        reset();
        setEmployeeData(null);
        setTimeout(() => {
          handleBackClick();
        }, 3000);
      } else {
        setError(res.message || "Clock request failed");
        setLabelMessage(res.message || "Clock request failed");
        setSubmitSuccess(false);
        setTimeout(() => {
          handleBackClick();
        }, 3000);
      }
    } else if (clockAction === "clockOut") {
      const res: any = await cloudClockOut(clockInRequest);
      if (res.status === "success") {
        setLabelMessage(`You have been successfully clocked out at: ${moment().format("h:mm A")}`);
        setSubmitSuccess(true);
        reset();
        setError("");
        setEmployeeData(null);
        setTimeout(() => {
          handleBackClick();
        }, 3000);
      } else {
        setError(res.message || "Clock request failed");
        setLabelMessage(res.message || "Clock request failed");
        setSubmitSuccess(false);
        setTimeout(() => {
          handleBackClick();
        }, 3000);
      }
    }
  };

  useEffect(() => {
    if (isSubmitSuccessful && isClockRequestSubmitted) {
      (async () => {
        try {
          await sendClockRequest(submittedAnswers);
        } finally {
          // cleanup
        }
      })();
    }
  }, [isSubmitSuccessful, isClockRequestSubmitted]);

  useEffect(() => {
    return () => {
      setEmployeeData(null);
      setError("");
      reset();
      clearErrors();
      setSubmittedAnswers([]);
      setIsClockRequestSubmitted(false);
      setClockAction("initial");
    };
  }, []);

  const fetchAndValidateEmployee = useCallback(
    async (employeeId: string) => {
      if (!employeeId) {
        setValidationError("Employee ID is required");
        return;
      }
      setValidationError("");
      try {
        const response = await FirebaseDataService.getEmployeeById(employeeId);
        console.log("Employee validation response:", response);
        if (response && response.data) {
          setEmployeeData(response.data);
          setValidationError("");
          setValue("firstName", response.data.firstName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });
          setValue("lastName", response.data.lastName || "", {
            shouldValidate: true,
            shouldDirty: true,
          });

          setClockin(getEmployeeClockedInState(response.data));
        } else {
          setEmployeeData(null);
          setValidationError("Not a valid employee ID");
        }
      } catch (error) {
        console.error("Employee validation error:", error);
        setEmployeeData(null);
        setValidationError("Error validating employee ID");
        setValue("firstName", "");
        setValue("lastName", "");
      }
    },
    [getEmployeeClockedInState, setValue],
  );

  const getData = async (code = "") => {
    setLoading(true);
    try {
      const { data } = await FirebaseDataService.getClientDataForNoAuthMode(clientId || code);
      if (!data) {
        // navigate("/404");
      }
      const { clientData, labelMessage, maximoTimezone } = data;
      dispatch(logInUser({ clientId: clientData.clientId, maximoTimezone: maximoTimezone || MAXIMO_TIME_ZONE }));
      setLabelMessage(labelMessage);
      if (clientData?.name) {
        setName(clientData.name);
      }
      if (clientData.logo) {
        try {
          const { data: logoUrl } = await FirebaseStorageService.getDownloadURL(
            process.env.REACT_APP_CLIENT_LOGO_FOLDER,
            clientData.logo,
          );
          setLogo(logoUrl);
        } catch (error) {
          // intentionally left empty as there is no handling required for a missing Client logo
        }
      }
    } catch (error) {
      debugError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData(clientId || "");
  }, []);

  const handleEmployeeIdValidation = useCallback(
    (employeeId: string) => {
      if (employeeId && employeeId.length > 0) {
        fetchAndValidateEmployee(employeeId);
      } else {
        setEmployeeData(null);
        setValidationError("");
      }
    },
    [fetchAndValidateEmployee],
  );

  const handleClockInClick = () => {
    setClockAction("clockIn");
    setError("");
    setSubmitSuccess(false);
  };

  const handleClockOutClick = () => {
    setClockAction("clockOut");
    setError("");
    setSubmitSuccess(false);
  };

  const handleBackClick = () => {
    setClockAction("initial");
    setError("");
    setSubmitSuccess(false);
    reset();
    setEmployeeData(null);
    setValidationError("");
  };

  return (
    <>
      <Container>
        {loading ? (
          <EmptyContainer>
            <Spinner animation="border" />
          </EmptyContainer>
        ) : (
          <>
            <Header name={name} logo={logo} noLink noUserLoginPage />

            {clockAction === "initial" ? (
              <Content>
                <ContentFrame $isMobile={isMobile}>
                  <Title>Time & Attendance</Title>
                  {/* <Subtitle>Please keep your employee ID ready</Subtitle> */}
                  <InputForm $isMobile={isMobile}>
                    <Input
                      controlId="employee-id"
                      type={InputTypes.text}
                      label="Employee ID"
                      validation={register("employeeId", {
                        required: {
                          value: true,
                          message: VALIDATION_MESSAGES.required,
                        },
                        onBlur: (e) => handleEmployeeIdValidation(e.target.value),
                      })}
                    />

                    {validationError && <ValidationError>{validationError}</ValidationError>}
                  </InputForm>

                  <ButtonContainer>
                    <ButtonWrapper>
                      <Button
                        text={"Next"}
                        disabled={!isValid || !!validationError || !employeeData}
                        loading={loading}
                        onClick={clockin ? handleClockOutClick : handleClockInClick}
                        additionalStyles={{ height: "48px" }}
                      />
                    </ButtonWrapper>
                  </ButtonContainer>
                </ContentFrame>
              </Content>
            ) : (
              <>
                <TimeAndAttendanceInputForm
                  key={clockAction}
                  locationName={locationName}
                  onSubmit={onSubmit}
                  register={register}
                  isValid={isValid}
                  loading={loading}
                  error={error}
                  errors={errors}
                  labelMessage={labelMessage}
                  validateEmployeeId={handleEmployeeIdValidation}
                  validationError={validationError}
                  setValue={setValue}
                  trigger={trigger}
                  submitSuccess={submitSuccess}
                  checklistsData={checklistsData}
                  clockAction={clockAction}
                  onBack={handleBackClick}
                  isToastVisible={submitSuccess || !!error}
                />
              </>
            )}

            <StickyFooter />
          </>
        )}
      </Container>

      <CustomToast
        success={submitSuccess}
        isToastVisible={submitSuccess || !!error}
        title={<strong>{submitSuccess ? "Success" : "Error"}</strong>}
        message={<p>{labelMessage}</p>}
      />
    </>
  );
};

const Container = styled.div`
  background-color: ${colors.white.default};
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  padding-bottom: 60px;
`;

const EmptyContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  padding-top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media screen and (max-width: 1369px) {
    padding-top: 100px;
  }
`;

const ContentFrame = styled.div<{ $isMobile?: boolean }>`
  padding: 30px;
  background-color: ${colors.white.default};
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "60%")};
`;

const StickyFooter = styled(Footer)`
  position: sticky;
  margin-top: auto;
`;

const ValidationError = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  font-family: "Roboto", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #333333;
  margin: 0 0 12px 0;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;
`;

// const Subtitle = styled.p`
//   font-family: "Roboto", sans-serif;
//   font-size: 16px;
//   font-weight: 400;
//   color: #666666;
//   margin: 0 0 24px 0;
//   text-align: center;
//   border-bottom: 1px solid #e0e0e0;
// `;
